import React, { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, IconButton, Tooltip, Badge } from "@mui/material";
import ForumIcon from "@mui/icons-material/Forum";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ContactsIcon from "@mui/icons-material/Contacts";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import GroupIcon from "@mui/icons-material/Group";
import BarChartIcon from "@mui/icons-material/BarChart";
import { canAccess } from "../../Utilities/accessControl";
import { useDispatch, useSelector } from "react-redux";
import { fetchUnreadCountFromAPI } from "../../Redux/NotificacionesDucks";

const SUB_MODULES = [
  {
    key: "smart_talking_conversaciones",
    path: "/smart_talking/conversaciones",
    label: "Conversaciones",
    icon: <ForumIcon />,
  },
  {
    key: "smart_talking_notificaciones",
    path: "/smart_talking/notificaciones",
    label: "Notificaciones",
    icon: <NotificationsIcon />,
  },
  {
    key: "smart_talking_cola_espera",
    path: "/smart_talking/cola_espera",
    label: "Cola de Espera",
    icon: <AccessTimeIcon />,
  },
  {
    key: "smart_talking_contactos",
    path: "/smart_talking/contactos",
    label: "Contactos",
    icon: <ContactsIcon />,
  },
  {
    key: "smart_talking_supervision",
    path: "/smart_talking/supervision",
    label: "Supervisión",
    icon: <SupervisorAccountIcon />,
  },
  {
    key: "smart_talking_gestion_carteras",
    module: "smart_talking_supervision", // mismo permiso que Supervisión (herramienta de supervisor)
    path: "/smart_talking/gestion_carteras",
    label: "Gestión de Carteras",
    icon: <GroupIcon />,
  },
  {
    key: "smart_talking_informes_ai",
    path: "/smart_talking/informes_ai",
    label: "Informes AI",
    icon: <BarChartIcon />,
  },
];

const SmartTalkingLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const unreadCount = useSelector((state) => state.notificaciones.unreadCount);
  const visibleModules = SUB_MODULES.filter((m) => canAccess(m.module ?? m.key));

  useEffect(() => {
    dispatch(fetchUnreadCountFromAPI());
    const interval = setInterval(
      () => dispatch(fetchUnreadCountFromAPI()),
      30_000,
    );
    return () => clearInterval(interval);
  }, [dispatch]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 97px)",
        mx: { xl: -4 },
        overflow: "hidden",
      }}
    >
      {/* Sidebar 56px */}
      <Box
        sx={{
          width: 56,
          borderRight: "1px solid #e0e0e0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 1,
          gap: 0.5,
          bgcolor: "#fff",
          flexShrink: 0,
        }}
      >
        {visibleModules.map((mod) => {
          const isActive = location.pathname.startsWith(mod.path);
          const isNotifications = mod.key === "smart_talking_notificaciones";
          return (
            <Tooltip key={mod.key} title={mod.label} placement="right">
              <IconButton
                onClick={() => navigate(mod.path)}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: isActive ? "#007AFF" : "transparent",
                  color: isActive ? "#fff" : "text.secondary",
                  "&:hover": {
                    bgcolor: isActive ? "#005ecb" : "#f0f0f0",
                  },
                }}
              >
                {isNotifications ? (
                  <Badge
                    badgeContent={unreadCount}
                    color="error"
                    max={99}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: 9,
                        minWidth: 16,
                        height: 16,
                        padding: "0 3px",
                      },
                    }}
                  >
                    {mod.icon}
                  </Badge>
                ) : (
                  mod.icon
                )}
              </IconButton>
            </Tooltip>
          );
        })}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default SmartTalkingLayout;
